import { FRAXLEND_PAIR_ABI } from '@/config/abis/FraxlendPair.abi'
import { FRAX_ORACLE_ABI } from '@/config/abis/fraxOracleAbi.abi'
import { QUERY_REFETCH_INTERVAL } from '@/config/consts'
import { calculateIsolatedHealthFactor } from '@/domain/common/calculations/healthFactor'
import { useChainConfigEntry } from '@/domain/hooks/useChainConfigEntry'
import { useAccount } from '@/domain/hooks/useAccount'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { erc20AbiWithApproveFnMalformed as ERC20_ABI } from '@/features/actions/flavours/approve/logic/normalizeErc20Abi'
import { queryOptions } from '@tanstack/react-query'
import BigNumber from 'bignumber.js'
import { Address, zeroAddress } from 'viem'
import { useChainId, useConfig } from 'wagmi'
import { multicall } from 'wagmi/actions'
import { useQuery } from '@tanstack/react-query'

export type IsolatedPairHealthInfo = {
  pairAddress: Address
  healthFactor: BigNumber
  userCollateralAmount: bigint
  userBorrowAmount: bigint
  collateralDecimals: number
  borrowDecimals: number
  exchangeRate: number
  normalizedExchangeRate: NormalizedUnitNumber
  maxLTV: Percentage
}

export type IsolatedHealthFactorResult = {
  pairs: IsolatedPairHealthInfo[]
  minHealthFactor?: BigNumber
  hasUnsafePositions: boolean
  isLoading: boolean
  isError: boolean
  error: Error | null
}

export function useIsolatedHealthFactor({ pairAddresses }: { pairAddresses: Address[] }): IsolatedHealthFactorResult {
  const account = useAccount()
  const chainId = useChainId()
  const { isolated } = useChainConfigEntry()
  const wagmiConfig = useConfig()

  const query = useQuery({
    ...isolatedHealthFactorQuery({
      account,
      chainId,
      pairAddresses,
      registryAddress: isolated?.registryAddress,
      wagmiConfig,
    }),
    refetchInterval: QUERY_REFETCH_INTERVAL,
  })

  return {
    ...query,
    pairs: query.data?.pairs || [],
    minHealthFactor: query.data?.minHealthFactor,
    hasUnsafePositions: query.data?.hasUnsafePositions || false,
  }
}

interface HealthFactorQueryParams {
  account?: Address
  chainId: number
  pairAddresses: Address[]
  registryAddress?: Address
  wagmiConfig: ReturnType<typeof useConfig>
}

type MulticallContract = {
  address: Address
  abi: readonly any[]
  functionName: string
  args?: readonly any[]
  enabled?: boolean
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function isolatedHealthFactorQuery({
  account,
  chainId,
  pairAddresses,
  registryAddress,
  wagmiConfig,
}: HealthFactorQueryParams) {
  return queryOptions({
    queryKey: ['isolatedHealthFactors', account, chainId, registryAddress],
    queryFn: async () => {
      if (!registryAddress || !account) {
        return { pairs: [], hasUnsafePositions: false }
      }

      if (!pairAddresses.length) {
        return { pairs: [], hasUnsafePositions: false }
      }

      // Step 2: Get user snapshots to determine which pairs have user positions
      const userSnapshotsResults = await multicall(wagmiConfig, {
        allowFailure: false,
        contracts: pairAddresses.map(
          (address) =>
            ({
              address,
              abi: FRAXLEND_PAIR_ABI,
              functionName: 'getUserSnapshot',
              args: [account],
            }) as const,
        ),
      })

      // Filter to pairs where user has position
      const relevantPairs = pairAddresses.filter((_, index) => {
        const snapshot = userSnapshotsResults[index] as [bigint, bigint, bigint]
        return snapshot[1] > 0n || snapshot[2] > 0n // Has borrow shares or collateral
      })

      if (!relevantPairs.length) {
        return { pairs: [], hasUnsafePositions: false }
      }

      // Step 3: Get all necessary contract data for each relevant pair
      const contractsData = await multicall(wagmiConfig, {
        allowFailure: false,
        contracts: relevantPairs.flatMap(
          (address) =>
            [
              // User data
              {
                address,
                abi: FRAXLEND_PAIR_ABI,
                functionName: 'getUserSnapshot',
                args: [account],
              },
              // Pair accounting
              {
                address,
                abi: FRAXLEND_PAIR_ABI,
                functionName: 'getPairAccounting',
              },
              // Constants
              {
                address,
                abi: FRAXLEND_PAIR_ABI,
                functionName: 'getConstants',
              },
              // Max LTV
              {
                address,
                abi: FRAXLEND_PAIR_ABI,
                functionName: 'maxLTV',
              },
              // Collateral contract
              {
                address,
                abi: FRAXLEND_PAIR_ABI,
                functionName: 'collateralContract',
              },
              // Asset (borrow) contract
              {
                address,
                abi: FRAXLEND_PAIR_ABI,
                functionName: 'asset',
              },
              // Exchange rate info
              {
                address,
                abi: FRAXLEND_PAIR_ABI,
                functionName: 'exchangeRateInfo',
              },
            ] as const,
        ),
      })

      // Step 4: Get token decimals and oracle prices
      const tokensAndOraclesData: MulticallContract[] = []

      for (let i = 0; i < relevantPairs.length; i++) {
        const baseIndex = i * 7
        const collateralContract = contractsData[baseIndex + 4] as Address
        const assetContract = contractsData[baseIndex + 5] as Address
        const exchangeRateInfo = contractsData[baseIndex + 6] as unknown as [Address, bigint, bigint, bigint, bigint]

        tokensAndOraclesData.push(
          // Collateral decimals
          {
            address: collateralContract,
            abi: ERC20_ABI,
            functionName: 'decimals',
          },
          // Borrow decimals
          {
            address: assetContract,
            abi: ERC20_ABI,
            functionName: 'decimals',
          },
          // Oracle prices if available
          {
            address: exchangeRateInfo[0],
            abi: FRAX_ORACLE_ABI,
            functionName: 'getPrices',
            enabled: exchangeRateInfo[0] !== zeroAddress,
          },
        )
      }

      const tokensAndOraclesResults = await multicall(wagmiConfig, {
        allowFailure: true,
        contracts: tokensAndOraclesData,
      })

      // Step 5: Calculate health factors for each pair
      const pairs: IsolatedPairHealthInfo[] = []

      for (let i = 0; i < relevantPairs.length; i++) {
        const baseIndex = i * 7
        const tokenBaseIndex = i * 3

        const pairAddress = relevantPairs[i] as Address
        const userSnapshot = contractsData[baseIndex] as [bigint, bigint, bigint]
        const accounting = contractsData[baseIndex + 1] as [bigint, bigint, bigint, bigint, bigint]
        const constants = contractsData[baseIndex + 2] as [
          bigint,
          bigint,
          bigint,
          bigint,
          bigint,
          bigint,
          bigint,
          bigint,
        ]
        const maxLTV = contractsData[baseIndex + 3] as bigint
        const exchangeRateInfo = contractsData[baseIndex + 6] as unknown as [Address, bigint, bigint, bigint, bigint]

        const collateralDecimalsResult = tokensAndOraclesResults[tokenBaseIndex]
        const borrowDecimalsResult = tokensAndOraclesResults[tokenBaseIndex + 1]
        const oraclePricesResult = tokensAndOraclesResults[tokenBaseIndex + 2]

        const collateralDecimals =
          collateralDecimalsResult?.status === 'success' ? (collateralDecimalsResult.result as number) : 18
        const borrowDecimals = borrowDecimalsResult?.status === 'success' ? (borrowDecimalsResult.result as number) : 18

        const [, , totalBorrow, totalBorrowShares] = accounting
        const userCollateralAmount = userSnapshot[2] || 0n
        const userBorrowShares = userSnapshot[1] || 0n

        const userBorrowAmount =
          userBorrowShares > 0n && totalBorrowShares > 0n ? (userBorrowShares * totalBorrow) / totalBorrowShares : 0n

        // Get exchange rate
        const [, , , lowExchangeRate] = exchangeRateInfo
        const oraclePrices =
          oraclePricesResult?.status === 'success'
            ? (oraclePricesResult.result as [boolean, bigint, bigint])
            : undefined
        const exchangePrecision = constants[4]

        const currentExchangeRate =
          (Number(oraclePrices ? Number(oraclePrices[1]) : Number(lowExchangeRate)) / Number(exchangePrecision)) *
          10 ** (borrowDecimals - collateralDecimals)

        // Calculate health factor
        const healthFactor = calculateIsolatedHealthFactor(
          NormalizedUnitNumber(Number(userCollateralAmount) / 10 ** collateralDecimals),
          NormalizedUnitNumber(Number(1 / currentExchangeRate)),
          NormalizedUnitNumber(Number(userBorrowAmount) / 10 ** borrowDecimals),
          Percentage(Number(maxLTV) / 100000, true, true),
        )

        pairs.push({
          pairAddress,
          healthFactor,
          userCollateralAmount,
          userBorrowAmount,
          collateralDecimals,
          borrowDecimals,
          exchangeRate: currentExchangeRate,
          normalizedExchangeRate: NormalizedUnitNumber(Number(1 / currentExchangeRate)),
          maxLTV: Percentage(Number(maxLTV) / 100000, true, true),
        })
      }

      // Calculate overall metrics
      const minHealthFactor = pairs.length
        ? pairs.reduce(
            (min, current) => (current.healthFactor.lt(min) ? current.healthFactor : min),
            pairs[0]?.healthFactor || BigNumber(0),
          )
        : undefined

      const hasUnsafePositions = pairs.some((pair) => pair.healthFactor.lt(1))

      return {
        pairs,
        minHealthFactor,
        hasUnsafePositions,
      }
    },
    enabled: !!account && !!registryAddress,
  })
}
